import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTrainerStats, getTrainerSessions, updateSessionStatus, coachCompleteSession, addCoachNote } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  DollarSign, Calendar, Star, Users, CheckCircle, 
  XCircle, Clock, ChevronRight, Shield, Award, MapPin, Wallet, ListOrdered
} from 'lucide-react';
import { CATEGORY_NAMES, formatDate, formatTime, formatCurrency, getInitials } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import EditTrainerProfileDialog from '../components/EditTrainerProfileDialog';
import StripeConnectPanel from '../components/StripeConnectPanel';
import TrainerAgreementModal from '../components/TrainerAgreementModal';
import TrainerSetupChecklist from '../components/TrainerSetupChecklist';
import ShareYourProfileCard from '../components/ShareYourProfileCard';
import TrainerOnlineToggle from '../components/TrainerOnlineToggle';
import TrainerIncomingRequests from '../components/TrainerIncomingRequests';
import TrainerPackagesPanel from '../components/TrainerPackagesPanel';
import TrainerEarningsBreakdown from '../components/TrainerEarningsBreakdown';
import GoOnlineToggle from '../components/GoOnlineToggle';
import MessagesPanel from '../components/MessagesPanel';
import CoachToolsPanel from '../components/CoachToolsPanel';
import SEO from '../components/SEO';

const TrainerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);

  const profile = user?.trainer_profile || {};

  // Show the independent-contractor agreement modal if this trainer hasn't accepted yet
  useEffect(() => {
    if (user && user.role === 'trainer' && !profile?.trainer_agreement_accepted_at) {
      setAgreementOpen(true);
    }
  }, [user, profile?.trainer_agreement_accepted_at]);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, sessionsRes] = await Promise.all([
        getTrainerStats(),
        getTrainerSessions(),
      ]);
      setStats(statsRes.data);
      setSessions(sessionsRes.data || []);
    } catch (error) {
      console.error('Failed to load dashboard data', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSessionAction = async (sessionId, status) => {
    setActionLoading(sessionId);
    try {
      if (status === 'completed') {
        const sess = sessions.find((x) => x.id === sessionId);
        await coachCompleteSession(sessionId, sess?.category);
        toast.success('Session marked complete, XP awarded.');
        // Prompt for a coach note (optional)
        try {
          const noteText = window.prompt(`Leave a note for ${sess?.client?.full_name || 'this client'}? (optional, max 500 chars)`);
          if (noteText && noteText.trim()) {
            await addCoachNote(sessionId, noteText.trim().slice(0, 500));
            toast.success('Note saved');
          }
        } catch { /* note flow optional */ }
      } else {
        await updateSessionStatus(sessionId, status);
        toast.success(`Session ${status}`);
      }
      loadData();
    } catch (error) {
      toast.error('Failed to update session');
    } finally {
      setActionLoading(null);
    }
  };

  const VERIFICATION_CONFIG = {
    verified: { icon: Shield, label: 'Verified', className: 'badge-verified' },
    elite: { icon: Award, label: 'Elite', className: 'badge-elite' },
    specialist: { icon: Star, label: 'Specialist', className: 'badge-specialist' },
  };

  const verification = VERIFICATION_CONFIG[profile.verification_level] || VERIFICATION_CONFIG.verified;
  const VerificationIcon = verification.icon;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const pendingSessions = sessions.filter(s => s.status === 'pending');
  const upcomingSessions = sessions.filter(s => s.status === 'confirmed');
  const recentSessions = sessions.filter(s => s.status === 'completed').slice(0, 5);

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="trainer-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-white">
                Welcome, {user?.full_name?.split(' ')[0]}
              </h1>
              <Badge className={`${verification.className} flex items-center gap-1`}>
                <VerificationIcon className="w-3 h-3" />
                {verification.label}
              </Badge>
            </div>
            <p className="text-zinc-400">Manage your sessions and track your earnings.</p>
          </div>
        </div>

        {/* Coach quick-access tiles for Earnings + My Listings */}
        <div className={`grid sm:grid-cols-2 gap-3 mb-8 ${agreementOpen ? 'opacity-40 pointer-events-none' : ''}`} data-testid="coach-quick-access">
          <button
            type="button"
            onClick={() => navigate('/dashboard/earnings')}
            disabled={agreementOpen}
            className="flex items-center gap-3 bg-[#18181B] border border-zinc-800 hover:border-[#C0A062]/40 rounded-lg p-4 text-left transition-colors disabled:cursor-not-allowed"
            data-testid="dash-earnings-link"
          >
            <div className="w-10 h-10 rounded-md bg-[#C0A062]/10 border border-[#C0A062]/40 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#C0A062]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold">Earnings</p>
              <p className="text-xs text-zinc-500">Payouts, balance, recent activity</p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600" />
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard/listings')}
            disabled={agreementOpen}
            className="flex items-center gap-3 bg-[#18181B] border border-zinc-800 hover:border-[#C0A062]/40 rounded-lg p-4 text-left transition-colors disabled:cursor-not-allowed"
            data-testid="dash-listings-link"
          >
            <div className="w-10 h-10 rounded-md bg-[#C0A062]/10 border border-[#C0A062]/40 flex items-center justify-center">
              <ListOrdered className="w-5 h-5 text-[#C0A062]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold">My Listings</p>
              <p className="text-xs text-zinc-500">Services, packages, group events</p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600" />
          </button>
        </div>

        {/* Setup checklist, auto-hides when complete */}
        <TrainerSetupChecklist user={user} onOpenEditProfile={() => setEditOpen(true)} />

        {/* Online toggle, big and obvious */}
        <TrainerOnlineToggle />

        {/* Incoming requests with Accept / Decline */}
        <TrainerIncomingRequests />

        {/* Session packages */}
        <div className="mb-8">
          <TrainerPackagesPanel />
        </div>

        {/* Coach tools: DG Points, XP settings, Challenges, Pending promotions */}
        <div className="mb-8">
          <CoachToolsPanel />
        </div>

        {/* Go online / live GPS toggle */}
        <div className="mb-8">
          <GoOnlineToggle />
        </div>

        {/* How you get paid (trainer-only) */}
        <div className="mb-8">
          <TrainerEarningsBreakdown defaultRate={75} />
        </div>

        {/* Messages */}
        <div className="mb-8">
          <MessagesPanel />
        </div>

        {/* Share-your-profile growth widget */}
        <ShareYourProfileCard user={user} />

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="stats-card p-6 rounded-lg" data-testid="stat-earnings">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-400 text-sm">Total Earnings</span>
              <DollarSign className="w-5 h-5 text-[#C0A062]" />
            </div>
            <p className="text-2xl font-bold text-white">
              {formatCurrency(stats?.total_earnings || 0)}
            </p>
          </div>

          <div className="stats-card p-6 rounded-lg" data-testid="stat-completed">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-400 text-sm">Completed Sessions</span>
              <CheckCircle className="w-5 h-5 text-[#C0A062]" />
            </div>
            <p className="text-2xl font-bold text-white">{stats?.completed_sessions || 0}</p>
          </div>

          <div className="stats-card p-6 rounded-lg" data-testid="stat-pending">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-400 text-sm">Pending Requests</span>
              <Clock className="w-5 h-5 text-[#C0A062]" />
            </div>
            <p className="text-2xl font-bold text-white">{stats?.pending_sessions || 0}</p>
          </div>

          <div className="stats-card p-6 rounded-lg" data-testid="stat-rating">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-400 text-sm">Average Rating</span>
              <Star className="w-5 h-5 text-[#C0A062]" />
            </div>
            <p className="text-2xl font-bold text-white">
              {stats?.avg_rating || 0} <span className="text-sm text-zinc-400">({stats?.total_reviews || 0})</span>
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Pending Requests */}
            {pendingSessions.length > 0 && (
              <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="pending-requests">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white">
                    Pending Requests ({pendingSessions.length})
                  </h2>
                </div>

                <div className="space-y-4">
                  {pendingSessions.map((session) => (
                    <div
                      key={session.id}
                      className="p-4 bg-zinc-800/30 border border-yellow-500/20 rounded-lg"
                      data-testid={`pending-${session.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10 border border-zinc-700">
                            <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-sm">
                              {getInitials(session.client?.full_name || 'C')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-medium text-white">{session.client?.full_name}</h3>
                            <p className="text-sm text-zinc-400">
                              {CATEGORY_NAMES[session.category]} • {formatDate(session.scheduled_date)} at {formatTime(session.scheduled_time)}
                            </p>
                            <p className="text-xs text-zinc-500 mt-1">{session.location_address}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#C0A062] font-bold">{formatCurrency(session.trainer_earnings)}</p>
                          <p className="text-xs text-zinc-500">your earnings</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button
                          size="sm"
                          className="flex-1 btn-primary"
                          onClick={() => handleSessionAction(session.id, 'confirmed')}
                          disabled={actionLoading === session.id}
                          data-testid={`accept-${session.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                          onClick={() => handleSessionAction(session.id, 'cancelled')}
                          disabled={actionLoading === session.id}
                          data-testid={`decline-${session.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Sessions */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="upcoming-sessions">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Upcoming Sessions</h2>
              </div>

              {upcomingSessions.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400">No upcoming sessions scheduled</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingSessions.map((session) => (
                    <div
                      key={session.id}
                      className="session-card p-4 rounded-lg flex items-center justify-between"
                      data-testid={`upcoming-${session.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10 border border-zinc-700">
                          <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-sm">
                            {getInitials(session.client?.full_name || 'C')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-medium text-white">{session.client?.full_name}</h3>
                          <p className="text-sm text-zinc-400">
                            {formatDate(session.scheduled_date)} at {formatTime(session.scheduled_time)}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="btn-primary"
                        onClick={() => handleSessionAction(session.id, 'completed')}
                        disabled={actionLoading === session.id}
                        data-testid={`complete-${session.id}`}
                      >
                        Mark Complete
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Sessions */}
            {recentSessions.length > 0 && (
              <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="recent-sessions">
                <h2 className="text-xl font-bold text-white mb-6">Recent Sessions</h2>
                <div className="space-y-3">
                  {recentSessions.map((session) => (
                    <div
                      key={session.id}
                      className="p-4 bg-zinc-800/30 rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10 border border-zinc-700">
                          <AvatarFallback className="bg-[#27272A] text-zinc-400 text-sm">
                            {getInitials(session.client?.full_name || 'C')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-medium text-white">{session.client?.full_name}</h3>
                          <p className="text-sm text-zinc-500">{formatDate(session.scheduled_date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-400 text-sm">{formatCurrency(session.trainer_earnings)}</span>
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Profile Summary */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="profile-summary">
              <div className="flex items-center gap-4 mb-5">
                <Avatar className="w-16 h-16 border-2 border-[#C0A062]/40">
                  <AvatarImage src={profile.profile_image} alt={user?.full_name} />
                  <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-lg font-bold">
                    {getInitials(user?.full_name || 'T')}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate" data-testid="profile-name">{user?.full_name}</p>
                  {(profile.service_area || user?.city) && (
                    <p className="text-xs text-zinc-400 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{profile.service_area || user?.city}</span>
                    </p>
                  )}
                </div>
              </div>

              <h2 className="text-lg font-bold text-white mb-4">Your Profile</h2>
              
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-zinc-400">Specialties</span>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="profile-specialties">
                    {((profile.specializations && profile.specializations.length > 0)
                      ? profile.specializations
                      : (profile.categories || []).map((c) => CATEGORY_NAMES[c] || c)
                    ).map((s) => (
                      <span key={s} className="text-xs px-2 py-1 bg-zinc-800 text-zinc-300 rounded">
                        {s}
                      </span>
                    ))}
                    {(!profile.specializations || profile.specializations.length === 0) &&
                      (!profile.categories || profile.categories.length === 0) && (
                        <span className="text-xs text-zinc-500">No specialties set</span>
                      )}
                  </div>
                </div>

                {profile.bio && (
                  <div>
                    <span className="text-sm text-zinc-400">Bio</span>
                    <p className="text-zinc-300 text-sm mt-1 line-clamp-3 whitespace-pre-line">{profile.bio}</p>
                  </div>
                )}

                <div>
                  <span className="text-sm text-zinc-400">Experience</span>
                  <p className="text-white mt-1">{profile.experience_years || 0} years</p>
                </div>

                <div>
                  <span className="text-sm text-zinc-400">Pricing per service</span>
                  <div className="mt-2 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-300">Personal Training</span>
                      <span className="text-white font-medium">
                        {profile.price_personal_training ? formatCurrency(profile.price_personal_training) : ', '}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-300">Self-Defense</span>
                      <span className="text-white font-medium">
                        {profile.price_self_defense ? formatCurrency(profile.price_self_defense) : ', '}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-300">Jiu-Jitsu</span>
                      <span className="text-white font-medium">
                        {profile.price_jiu_jitsu ? formatCurrency(profile.price_jiu_jitsu) : ', '}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <Button 
                variant="outline" 
                className="w-full mt-6 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                onClick={() => setEditOpen(true)}
                data-testid="edit-profile-btn"
              >
                Edit Profile
              </Button>
            </div>

            {/* Stripe Connect, receive payouts */}
            <StripeConnectPanel />

            {/* Earnings Summary */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6">
              <h2 className="text-lg font-bold text-white mb-4">This Month</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">Sessions</span>
                  <span className="text-white font-medium">{stats?.completed_sessions || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">Earnings</span>
                  <span className="text-[#C0A062] font-bold">{formatCurrency(stats?.total_earnings || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <EditTrainerProfileDialog open={editOpen} onOpenChange={setEditOpen} />
      <TrainerAgreementModal
        open={agreementOpen}
        onAccept={() => setAgreementOpen(false)}
      />
    </div>
  );
};

export default TrainerDashboard;
